import { type Leg, type Money } from '@opentripplanner/types';
import coreUtils from '@opentripplanner/core-utils';
import {
  type RiderCategory,
  AERIAL_TRAM_ROUTE_ID_LABEL,
  FARE_MEDIUM_IDS,
} from '#/shared/constants';

interface LegCostInfo {
  price: Money | undefined;
  isDependent: boolean;
}

/**
 * Detects if a leg fare represents an upgrade or dependent pricing scenario
 * where the amount is non-zero but reduced due to dependency on a prior leg's fare
 * @param legCostInfo The leg cost information to check
 * @returns true if this is a dependent/upgrade scenario with a non-zero reduced amount
 */
export const isUpgradeOrDependentTransfer = (
  legCostInfo: LegCostInfo | null | undefined
): boolean => {
  if (!legCostInfo?.price) {
    return false;
  }
  return legCostInfo.isDependent && legCostInfo.price.amount > 0;
};

interface ItineraryCostBreakdown {
  legCosts: Record<string, LegCostInfo>;
  totalCost: Money;
}

interface FareCalculationIds {
  mediumId: string | null;
  riderCategoryId: string | null;
}

type AllCategoriesCostBreakdown = Record<RiderCategory, ItineraryCostBreakdown>;

/**
 * Gets the appropriate medium ID and rider category for fare calculation
 * The Aerial Tram has special fare handling compared to other routes
 * @return Object with mediumId and riderCategoryId to use for fare calculation
 */
export const getFareCalculationIds = (
  routeId: string | number | null,
  mediumId: string | null,
  riderCategoryId: string | null
): FareCalculationIds => {
  if (routeId != null && routeId === AERIAL_TRAM_ROUTE_ID_LABEL) {
    // Aerial Tram uses cash payment (medium ID '0') regardless of user selection
    return { mediumId: '0', riderCategoryId: null };
  }
  return { mediumId, riderCategoryId };
};

/**
 * Returns detailed itinerary cost breakdown by trip ID and total cost.
 * Uses getLegCost from core-utils which handles transfer detection via seenFareIds.
 * @param legs Itinerary legs with fare products (must have used getLegsWithFares)
 * @param mediumId Desired medium ID to calculate fare for
 * @param riderCategoryId Desired rider category to calculate fare for
 * @returns Object with leg costs by trip ID and total cost, or undefined if no costs found
 */
export function getDetailedItineraryCost(
  legs: Leg[],
  mediumId: string | null,
  riderCategoryId: RiderCategory | null
): ItineraryCostBreakdown | undefined {
  const legCostsByTripId: Record<string, LegCostInfo> = {};
  const seenFareIds: string[] = [];
  let totalAmount = 0;
  let currency: Money['currency'] = { code: '', digits: 2 };

  legs
    .filter(leg => leg.fareProducts?.length && leg.trip?.id)
    .forEach(leg => {
      // Handle Aerial Tram special case first
      const routeId = typeof leg?.route === 'object' ? leg.route?.id : null;
      const { mediumId: calcMediumId, riderCategoryId: calcCategoryId } =
        getFareCalculationIds(routeId, mediumId, riderCategoryId);

      // Try to find a fare using the preferred medium first, then fall back to other media
      // This ensures regional agencies like SAM (cash only) can display fares
      const mediumIdsToTry = calcMediumId
        ? [calcMediumId, ...FARE_MEDIUM_IDS]
        : FARE_MEDIUM_IDS;

      // Find the first valid fare cost by trying each medium ID
      const cost = mediumIdsToTry.reduce<ReturnType<
        typeof coreUtils.itinerary.getLegCost
      > | null>((found, tryMediumId) => {
        if (found?.appliedFareProduct?.legPrice) return found;
        const result = coreUtils.itinerary.getLegCost(
          leg,
          tryMediumId,
          calcCategoryId,
          seenFareIds
        );
        return result?.appliedFareProduct?.legPrice ? result : null;
      }, null);

      if (!cost?.appliedFareProduct?.legPrice || !leg.trip?.id) return;

      const tripId = leg.trip.id;
      const { appliedFareProduct, productUseId, isDependent } = cost;
      const { legPrice } = appliedFareProduct;

      // Track this fare product ID for future legs to detect transfers
      // getLegCost uses seenFareIds to return zero price for transfer legs
      if (productUseId) {
        seenFareIds.push(productUseId);
      }

      // Store the leg cost info - legPrice already accounts for transfers
      legCostsByTripId[tripId] = {
        price: legPrice,
        isDependent: isDependent || false,
      };

      // Accumulate total from the already-adjusted legPrice
      totalAmount += Number(legPrice.amount);
      currency = legPrice.currency;
    });

  if (Object.keys(legCostsByTripId).length === 0) return undefined;

  return {
    legCosts: legCostsByTripId,
    totalCost: { amount: totalAmount, currency },
  };
}
/**
 * Returns detailed itinerary cost breakdown for all rider categories by trip ID and total cost.
 * @param legs Itinerary legs with fare products (must have used getLegsWithFares)
 * @param mediumId Desired medium ID to calculate fare for
 * @param riderCategoryIds Array of rider categories to calculate fare for
 * @returns Object with leg costs by trip ID and total cost for each rider category, or undefined if no costs found
 */
export function getDetailedItineraryCostForAllCategories(
  legs: Leg[],
  mediumId: string | null,
  riderCategoryIds: RiderCategory[]
): AllCategoriesCostBreakdown | undefined {
  if (riderCategoryIds.length === 0) return undefined;

  const results = riderCategoryIds.reduce<AllCategoriesCostBreakdown>(
    (acc, riderCategoryId) => {
      const costData = getDetailedItineraryCost(
        legs,
        mediumId,
        riderCategoryId
      );
      if (costData) {
        return { ...acc, [riderCategoryId]: costData };
      }
      return acc;
    },
    {} as AllCategoriesCostBreakdown
  );

  return Object.keys(results).length > 0 ? results : undefined;
}
